import { apiClient } from '@neighbourhood-munchies/api-client';
import { User as SupabaseUser } from '@supabase/supabase-js';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { clearAuthData, isSessionExpired } from '../utils/sessionManager';

export interface UserProfile {
  id: string;
  name: string;
  email: string | null;
  phone: string;
  address: string | null;
  deliveryZoneId: string | null;
  deliveryZoneName: string | null;
  userType: 'customer' | 'guest';
  createdAt: string;
  updatedAt: string;
}

export interface RegistrationData {
  phone: string;
  name: string;
  deliveryZoneId?: string;
  address?: string;
  email?: string;
}

export interface ProfileUpdateData {
  name?: string;
  deliveryZoneId?: string;
  address?: string;
  // phone explicitly excluded
}

interface AuthContextType {
  user: UserProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  needsProfileSetup: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  signUp: (email: string, password: string, name: string, phone: string) => Promise<void>;
  createGuestSession: () => Promise<void>;
  signInWithPhone: (phone: string) => Promise<void>;
  registerWithProfile: (data: RegistrationData) => Promise<void>;
  updateProfile: (data: ProfileUpdateData) => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [needsProfileSetup, setNeedsProfileSetup] = useState(false);

  // Transform Supabase user to our UserProfile interface
  const transformUser = (supabaseUser: SupabaseUser): UserProfile => ({
    id: supabaseUser.id,
    name: supabaseUser.user_metadata?.name || supabaseUser.email?.split('@')[0] || 'User',
    email: supabaseUser.email || null,
    phone: supabaseUser.user_metadata?.phone || supabaseUser.phone || '',
    address: supabaseUser.user_metadata?.address || null,
    deliveryZoneId: supabaseUser.user_metadata?.deliveryZoneId || null,
    deliveryZoneName: supabaseUser.user_metadata?.deliveryZoneName || null,
    userType: supabaseUser.user_metadata?.is_guest ? 'guest' : 'customer',
    createdAt: supabaseUser.created_at,
    updatedAt: supabaseUser.updated_at || supabaseUser.created_at,
  });

  // Initialize auth state and restore session on app initialization
  // Requirements: 12.2 - Restore session on app initialization
  useEffect(() => {
    console.log('[AuthContext] Initializing auth state and restoring session');

    // Get initial session from storage
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      // Check if session is expired - Requirements: 12.3
      if (session && isSessionExpired(session)) {
        console.log('[AuthContext] Session expired, signing out');
        await supabase.auth.signOut();
        clearAuthData();
        setUser(null);
        apiClient.setAuthToken(null);
        setIsLoading(false);
        return;
      }

      if (session?.user) {
        console.log('[AuthContext] Session restored successfully');
        apiClient.setAuthToken(session.access_token);
        try {
          const profileRes = await apiClient.users.getProfile();
          if (profileRes.success && profileRes.data) {
            setUser(profileRes.data as unknown as UserProfile);
            setNeedsProfileSetup(false);
          } else {
            setNeedsProfileSetup(true);
            setUser(transformUser(session.user));
          }
        } catch {
          setUser(transformUser(session.user));
        }
      } else {
        console.log('[AuthContext] No existing session found');
      }
      setIsLoading(false);
    });

    // Listen for auth state changes
    // Requirements: 12.3 - Handle session expiration gracefully
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('[AuthContext] Auth state changed:', event);

      // Handle token refresh - Requirements: 12.1
      if (event === 'TOKEN_REFRESHED') {
        console.log('[AuthContext] Token refreshed successfully');
        if (session?.user) {
          const transformedUser = transformUser(session.user);
          setUser(transformedUser);
          apiClient.setAuthToken(session.access_token);
        }
      }
      // Handle sign out - Requirements: 12.4
      else if (event === 'SIGNED_OUT') {
        console.log('[AuthContext] User signed out, clearing authentication data');
        clearAuthData();
        setUser(null);
        apiClient.setAuthToken(null);
        setIsLoading(false);
        return;
      }
      // Handle sign in
      else if (event === 'SIGNED_IN') {
        console.log('[AuthContext] User signed in');
        if (session?.user) {
          apiClient.setAuthToken(session.access_token);
          try {
            const profileRes = await apiClient.users.getProfile();
            if (profileRes.success && profileRes.data) {
              setNeedsProfileSetup(false);
              setUser(profileRes.data as unknown as UserProfile);
            } else {
              setNeedsProfileSetup(true);
              setUser(transformUser(session.user));
            }
          } catch {
            setNeedsProfileSetup(true);
            setUser(transformUser(session.user));
          }
        }
      }
      // Handle user update
      else if (event === 'USER_UPDATED') {
        console.log('[AuthContext] User data updated');
        if (session?.user) {
          const transformedUser = transformUser(session.user);
          setUser(transformedUser);
        }
      }

      // Sync auth token for all events not already handled above
      if (session?.access_token) {
        apiClient.setAuthToken(session.access_token);
      }
      setIsLoading(false);
    });

    return () => {
      console.log('[AuthContext] Cleaning up auth state listener');
      subscription.unsubscribe();
    };
  }, []);

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      if (data.user) {
        const transformedUser = transformUser(data.user);
        setUser(transformedUser);
        if (data.session) {
          apiClient.setAuthToken(data.session.access_token);
        }
      }
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const signUp = async (email: string, password: string, name: string, phone: string) => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name,
            phone,
          },
        },
      });

      if (error) throw error;

      if (data.user) {
        const transformedUser = transformUser(data.user);
        setUser(transformedUser);
        if (data.session) {
          apiClient.setAuthToken(data.session.access_token);
        }
      }
    } catch (error) {
      console.error('Sign up failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const createGuestSession = async () => {
    setIsLoading(true);
    try {
      // Create a guest user with anonymous sign-in
      const guestEmail = `guest-${Date.now()}@temp.com`;
      const guestPassword = `guest-${Math.random().toString(36).substring(2, 15)}`;

      const { data, error } = await supabase.auth.signUp({
        email: guestEmail,
        password: guestPassword,
        options: {
          data: {
            name: 'Guest User',
            phone: '',
            is_guest: true,
          },
        },
      });

      if (error) throw error;

      if (data.user) {
        const transformedUser = transformUser(data.user);
        setUser(transformedUser);
        if (data.session) {
          apiClient.setAuthToken(data.session.access_token);
        }
      }
    } catch (error) {
      console.error('Guest session creation failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      console.log('[AuthContext] Logging out user');

      // Invalidate session token with Supabase - Requirements: 12.4
      await supabase.auth.signOut();

      // Clear user state
      setUser(null);

      // Clear auth token from API client
      apiClient.setAuthToken(null);

      // Clear all authentication data from storage - Requirements: 12.4
      clearAuthData();

      console.log('[AuthContext] User logged out successfully, session cleared');
    } catch (error) {
      console.error('[AuthContext] Logout failed:', error);
      // Even if logout fails, clear local state - Requirements: 12.4
      setUser(null);
      apiClient.setAuthToken(null);
      clearAuthData();
    }
  };

  const signInWithPhone = async (phone: string) => {
    setIsLoading(true);
    try {
      const result = await apiClient.auth.phoneLogin(phone);
      if (!result.success) throw new Error(result.message || 'Phone login failed');

      const { session } = result.data as { session: { access_token: string; refresh_token: string } };

      const { data, error } = await supabase.auth.setSession({
        access_token: session.access_token,
        refresh_token: session.refresh_token,
      });

      if (error) throw error;

      if (data.user) {
        apiClient.setAuthToken(session.access_token);
        try {
          const profileRes = await apiClient.users.getProfile();
          if (profileRes.success && profileRes.data) {
            setNeedsProfileSetup(false);
            setUser(profileRes.data as unknown as UserProfile);
          } else {
            setNeedsProfileSetup(true);
            setUser(transformUser(data.user));
          }
        } catch {
          setNeedsProfileSetup(true);
          setUser(transformUser(data.user));
        }
      }
    } catch (error) {
      console.error('Phone sign in failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const registerWithProfile = async (data: RegistrationData) => {
    setIsLoading(true);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session) {
        throw new Error('User must be signed in before completing registration');
      }

      // Call backend API to create user profile with full data
      const response = await apiClient.users.create({
        name: data.name,
        phone: data.phone,
        email: data.email,
        address: data.address,
        deliveryZoneId: data.deliveryZoneId,
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to create user profile');
      }

      setNeedsProfileSetup(false);
      // Refresh from DB — source of truth
      await refreshProfile();
    } catch (error) {
      console.error('Registration with profile failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateProfile = async (data: ProfileUpdateData) => {
    setIsLoading(true);
    try {
      if (!user) {
        throw new Error('User must be authenticated to update profile');
      }

      // Validate that phone number is not being updated
      if ('phone' in data) {
        throw new Error('Phone number cannot be updated');
      }

      // Call backend API to update user profile
      const response = await apiClient.users.update(user.id, {
        name: data.name,
        address: data.address,
        deliveryZoneId: data.deliveryZoneId,
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to update user profile');
      }

      // Refresh from DB — source of truth
      await refreshProfile();
    } catch (error) {
      console.error('Profile update failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const refreshProfile = async () => {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (session?.user) {
        try {
          const profileRes = await apiClient.users.getProfile();
          if (profileRes.success && profileRes.data) {
            setUser(profileRes.data as unknown as UserProfile);
            setNeedsProfileSetup(false);
            return;
          }
        } catch {
          // No DB profile yet — fall back to Supabase data
        }
        setUser(transformUser(session.user));
      }
    } catch (error) {
      console.error('Failed to refresh profile:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    needsProfileSetup,
    login,
    logout,
    signUp,
    createGuestSession,
    signInWithPhone,
    registerWithProfile,
    updateProfile,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
